<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
    ZhEmptyState,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhUsageMeter,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api, type StaffCoupon, type StaffCredit, type StaffEnterpriseDetail, type StaffPlanInput } from '@/api'
import {
    billingWords,
    couponWords,
    creditCentsOf,
    creditWords,
    dayOf,
    dollars,
    planSourceWords,
    removalWords,
    staffActionWords,
    staffChangeWords,
} from '@/lib/staffEnterprises'

/**
 * One enterprise's Staff page (#394): its plan, billing and usage, its FloPay subscription and invoices, its
 * reconciliation, and every action staff took on it. Staff set its plan (by hand, or through FloPay), extend its
 * period, pause and unpause it, close and reopen it, and apply and remove FloPay coupons and one-off credits (#399),
 * each with a reason that stays in the staff record, from here. A refusal is shown as the API said it. Nothing of
 * the tenant's own work (runs, memory, mail, secrets) is read here.
 */
const route = useRoute()
const id = computed(() => String(route.params['id'] ?? ''))

const detail = ref<StaffEnterpriseDetail | null>(null)
const loading = ref(true)
const pageError = ref<string | null>(null)
const busy = ref(false)
/** The refusal of the action in the open dialog, as the API worded it. */
const actionError = ref<string | null>(null)
/** What the last coupon or credit action did, said once it is done (#399): `Coupon applied.` */
const notice = ref<string | null>(null)

/** The dialog open now, or null. */
const dialog = ref<'pause' | 'plan' | 'extend' | 'close' | 'coupon' | 'credit' | 'remove' | null>(null)
const reason = ref('')
const planId = ref('enterprise')
const planKind = ref<StaffPlanInput['kind']>('quoted')
const priceDollars = ref('')
const endsOn = ref('')
const confirmName = ref('')
const couponId = ref('')
const creditAmount = ref('')
/**
 * The id the Add credit dialog sends its credit by (#399): made at the first submit and sent again with every resend of
 * the same amount, so a credit FloPay made but whose answer was lost is not made twice. A new dialog or a new amount
 * is a new credit, with a new id.
 */
const creditRequestId = ref<string | null>(null)
watch(creditAmount, () => {
    creditRequestId.value = null
})
/** The coupon or credit the remove dialog takes off, or null before one is asked for. */
const removing = ref<{ kind: 'coupon'; coupon: StaffCoupon } | { kind: 'credit'; credit: StaffCredit } | null>(null)

/** The coupons and credits on its FloPay subscription (#399); none where the API sent neither. */
const coupons = computed(() => detail.value?.coupons ?? [])
const credits = computed(() => detail.value?.credits ?? [])

/** The most a reason holds (the `staff_actions.reason` column), which a coupon or credit action requires (#399). */
const REASON_LIMIT = 500
const reasonReady = computed(() => reason.value.trim() !== '' && reason.value.trim().length <= REASON_LIMIT)
/** Each coupon and credit dialog sends only once what the API requires is there, so staff are not refused for it. */
const couponReady = computed(() => couponId.value.trim() !== '' && reasonReady.value)
const creditReady = computed(() => creditCentsOf(creditAmount.value) !== null && reasonReady.value)

/** The remove dialog's title: the kind and id, then words staff tell it apart by. */
const removingTitle = computed(() => {
    const target = removing.value
    if (!target) {
        return ''
    }
    const [head, words] =
        target.kind === 'coupon'
            ? [`Remove coupon ${target.coupon.id}`, couponWords(target.coupon)]
            : [`Remove credit ${target.credit.id}`, creditWords(target.credit)]
    return words ? `${head}: ${words}` : head
})

const PLANS: { id: string; name: string }[] = [
    { id: 'free', name: 'Free' },
    { id: 'starter', name: 'Starter' },
    { id: 'company', name: 'Company' },
    { id: 'scale', name: 'Scale' },
    { id: 'enterprise', name: 'Enterprise' },
]

const KINDS: { id: StaffPlanInput['kind']; name: string }[] = [
    { id: 'quoted', name: 'Quoted price, by hand' },
    { id: 'comped', name: 'Comped, by hand' },
    { id: 'trial', name: 'Trial until a date, by hand' },
    { id: 'flopay', name: 'Through FloPay' },
]

const open = computed(() => detail.value !== null && detail.value.closed === null)

function message(failure: unknown, fallback: string): string {
    return failure instanceof Error && failure.message ? failure.message : fallback
}

/** A date typed as a day, as the start of that day in UTC. */
function isoOfDay(day: string): string | undefined {
    return day ? new Date(`${day}T00:00:00.000Z`).toISOString() : undefined
}

async function load(): Promise<void> {
    try {
        detail.value = await api.staffEnterprise(id.value)
        pageError.value = null
    } catch (failure) {
        pageError.value = message(failure, 'Could not load this enterprise.')
    } finally {
        loading.value = false
    }
}

function openDialog(which: NonNullable<typeof dialog.value>): void {
    actionError.value = null
    notice.value = null
    reason.value = ''
    endsOn.value = ''
    confirmName.value = ''
    priceDollars.value = ''
    couponId.value = ''
    creditAmount.value = ''
    creditRequestId.value = null
    dialog.value = which
}

/** Opens the remove dialog for one coupon or credit: staff see what goes and say why before it does. */
function askToRemove(target: NonNullable<typeof removing.value>): void {
    openDialog('remove')
    removing.value = target
}

function closeDialog(next: boolean): void {
    if (!next) {
        dialog.value = null
    }
}

/**
 * Runs one action: on success the dialog closes, the page is read again and `done`, where given, says what happened;
 * a refusal stays where it was asked.
 */
async function act(call: () => Promise<unknown>, fallback: string, inDialog: boolean, done?: string): Promise<void> {
    busy.value = true
    actionError.value = null
    pageError.value = null
    notice.value = null
    try {
        await call()
        dialog.value = null
        await load()
        notice.value = done ?? null
    } catch (failure) {
        if (inDialog) {
            actionError.value = message(failure, fallback)
        } else {
            pageError.value = message(failure, fallback)
        }
    } finally {
        busy.value = false
    }
}

function pause(): Promise<void> {
    const typed = reason.value.trim()
    return act(() => api.staffPauseEnterprise(id.value, typed ? { reason: typed } : {}), 'Could not pause it.', true)
}

function unpause(): Promise<void> {
    return act(() => api.staffUnpauseEnterprise(id.value), 'Could not unpause it.', false)
}

function setPlan(): Promise<void> {
    const body: StaffPlanInput = { planId: planId.value, kind: planKind.value }
    if (planKind.value === 'quoted') {
        body.quotedMonthlyPriceCents = Math.round(Number(priceDollars.value) * 100)
    }
    if (planKind.value === 'trial') {
        const endsAt = isoOfDay(endsOn.value)
        if (endsAt) {
            body.endsAt = endsAt
        }
    }
    if (reason.value.trim()) {
        body.reason = reason.value.trim()
    }
    return act(() => api.staffSetPlan(id.value, body), 'Could not set the plan.', true)
}

function extend(): Promise<void> {
    return act(
        () => api.staffExtendEnterprise(id.value, { endsAt: isoOfDay(endsOn.value) ?? '' }),
        'Could not extend the period.',
        true,
    )
}

function close(): Promise<void> {
    return act(
        () => api.staffCloseEnterprise(id.value, { confirmName: confirmName.value }),
        'Could not close it.',
        true,
    )
}

function reopen(): Promise<void> {
    return act(() => api.staffCancelCloseEnterprise(id.value), 'Could not reopen it.', false)
}

function applyCoupon(): Promise<void> {
    return act(
        () => api.staffApplyCoupon(id.value, { couponId: couponId.value.trim(), reason: reason.value.trim() }),
        'Could not apply the coupon.',
        true,
        'Coupon applied.',
    )
}

function addCredit(): Promise<void> {
    creditRequestId.value ??= crypto.randomUUID()
    const requestId = creditRequestId.value
    return act(
        () =>
            api.staffApplyCredit(id.value, {
                amountCents: creditCentsOf(creditAmount.value) ?? 0,
                reason: reason.value.trim(),
                requestId,
            }),
        'Could not add the credit.',
        true,
        'Credit added.',
    )
}

function remove(): Promise<void> {
    const target = removing.value
    const body = { reason: reason.value.trim() }
    if (target?.kind === 'coupon') {
        return act(
            () => api.staffRemoveCoupon(id.value, target.coupon.id, body),
            'Could not remove the coupon.',
            true,
            'Coupon removed.',
        )
    }
    if (target?.kind === 'credit') {
        return act(
            () => api.staffRemoveCredit(id.value, target.credit.id, body),
            'Could not remove the credit.',
            true,
            'Credit removed.',
        )
    }
    return Promise.resolve()
}

onMounted(load)
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<RouterLink to="/staff/enterprises" class="text-muted-foreground text-sm underline underline-offset-4">
			All enterprises
		</RouterLink>
		<p v-if="pageError" class="text-destructive text-sm" role="alert" data-testid="staff-enterprise-error">
			{{ pageError }}
		</p>
		<p v-if="notice" class="text-sm" role="status" data-testid="staff-notice">{{ notice }}</p>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>

		<template v-if="detail">
			<ZhPageTitle kicker="Staff">{{ detail.name }}</ZhPageTitle>
			<div class="flex flex-wrap items-center gap-2">
				<ZhStatusPill v-if="detail.closed" tone="failed" :pulse="false">
					Closed, erased {{ dayOf(detail.closed.purgeAt) || 'never' }}
				</ZhStatusPill>
				<ZhStatusPill v-else-if="detail.paused" tone="action" :pulse="false">Paused</ZhStatusPill>
				<ZhStatusPill v-else tone="completed" :pulse="false">Open</ZhStatusPill>
				<span class="grow" />
				<template v-if="open">
					<Button type="button" variant="outline" size="sm" :disabled="busy" data-testid="staff-set-plan" @click="openDialog('plan')">
						Set plan
					</Button>
					<Button type="button" variant="outline" size="sm" :disabled="busy" data-testid="staff-extend" @click="openDialog('extend')">
						Extend
					</Button>
					<Button
						v-if="detail.paused"
						type="button"
						variant="outline"
						size="sm"
						:disabled="busy"
						data-testid="staff-unpause"
						@click="unpause"
					>
						Unpause
					</Button>
					<Button
						v-else
						type="button"
						variant="outline"
						size="sm"
						:disabled="busy"
						data-testid="staff-pause"
						@click="openDialog('pause')"
					>
						Pause
					</Button>
					<Button type="button" variant="destructive" size="sm" :disabled="busy" data-testid="staff-close" @click="openDialog('close')">
						Close
					</Button>
				</template>
				<Button v-else type="button" variant="outline" size="sm" :disabled="busy" data-testid="staff-reopen" @click="reopen">
					Reopen
				</Button>
			</div>

			<ZhPanel class="space-y-2">
				<ZhSectionHeading as="h2">Plan and billing</ZhSectionHeading>
				<dl>
					<ZhKeyValue label="Plan">{{ detail.plan.name }}</ZhKeyValue>
					<ZhKeyValue label="Set">{{ planSourceWords(detail.planSource) }}</ZhKeyValue>
					<ZhKeyValue label="Billing">{{ billingWords(detail.billing) }}</ZhKeyValue>
					<ZhKeyValue label="Subscription">
						<template v-if="detail.subscription">{{ detail.subscription.id }} ({{ detail.subscription.status }})</template>
						<template v-else>None</template>
					</ZhKeyValue>
				</dl>
			</ZhPanel>

			<ZhPanel class="space-y-3">
				<ZhSectionHeading as="h2">Usage this period</ZhSectionHeading>
				<ZhUsageMeter :used="detail.usage.portalRuns.used" :included="detail.usage.portalRuns.limit" unit="portal runs" />
				<ZhUsageMeter
					:used="detail.usage.runnerMinutes.used"
					:included="detail.usage.runnerMinutes.limit"
					unit="runner minutes"
				/>
				<ZhUsageMeter :used="detail.usage.members.used" :included="detail.usage.members.limit" unit="members" />
				<ZhUsageMeter :used="detail.usage.storage.used" :included="detail.usage.storage.limit" unit="bytes stored" />
			</ZhPanel>

			<ZhPanel class="space-y-2">
				<ZhSectionHeading as="h2">Invoices</ZhSectionHeading>
				<p v-if="detail.invoices === 'unavailable'" class="text-sm" data-testid="staff-invoices-unavailable">
					The invoices are unavailable: FloPay did not answer. The rest of this page is up to date.
				</p>
				<Table v-else-if="detail.invoices.length">
					<TableHeader>
						<TableRow>
							<TableHead>Invoice</TableHead>
							<TableHead>Amount</TableHead>
							<TableHead>Status</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="invoice in detail.invoices" :key="invoice.id" data-testid="staff-invoice-row">
							<TableCell><code>{{ invoice.id }}</code></TableCell>
							<TableCell>{{ dollars(invoice.amountCents) }}</TableCell>
							<TableCell>{{ invoice.status }}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else>No invoices.</ZhEmptyState>
			</ZhPanel>

			<ZhPanel class="space-y-3" data-testid="staff-coupons-credits">
				<div class="flex flex-wrap items-center gap-2">
					<ZhSectionHeading as="h2">Coupons and credits</ZhSectionHeading>
					<span class="grow" />
					<template v-if="open">
						<Button type="button" variant="outline" size="sm" :disabled="busy" data-testid="staff-apply-coupon" @click="openDialog('coupon')">
							Apply coupon
						</Button>
						<Button type="button" variant="outline" size="sm" :disabled="busy" data-testid="staff-add-credit" @click="openDialog('credit')">
							Add credit
						</Button>
					</template>
				</div>

				<ZhSectionHeading as="h3">Coupons</ZhSectionHeading>
				<p v-if="coupons === 'unavailable'" class="text-sm" data-testid="staff-coupons-unavailable">
					The coupons are unavailable: FloPay did not answer. The rest of this page is up to date; reload it
					later to try again.
				</p>
				<Table v-else-if="coupons.length">
					<TableHeader>
						<TableRow>
							<TableHead>Coupon</TableHead>
							<TableHead>Discount</TableHead>
							<TableHead>Ends</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="coupon in coupons" :key="coupon.id" data-testid="staff-coupon-row">
							<TableCell>{{ coupon.name }} <code>{{ coupon.id }}</code></TableCell>
							<TableCell>{{ coupon.discount }}</TableCell>
							<TableCell>{{ dayOf(coupon.endsAt) || 'No end' }}</TableCell>
							<TableCell>
								<Button
									v-if="open"
									type="button"
									variant="outline"
									size="sm"
									:disabled="busy"
									data-testid="staff-remove-coupon"
									@click="askToRemove({ kind: 'coupon', coupon })"
								>
									Remove
								</Button>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else compact data-testid="staff-coupons-empty">No coupons.</ZhEmptyState>

				<ZhSectionHeading as="h3">Credits</ZhSectionHeading>
				<p v-if="credits === 'unavailable'" class="text-sm" data-testid="staff-credits-unavailable">
					The credits are unavailable: FloPay did not answer. The rest of this page is up to date; reload it
					later to try again.
				</p>
				<Table v-else-if="credits.length">
					<TableHeader>
						<TableRow>
							<TableHead>Credit</TableHead>
							<TableHead>Left</TableHead>
							<TableHead>FloPay id</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="credit in credits" :key="credit.id" data-testid="staff-credit-row">
							<TableCell>{{ creditWords(credit) }}</TableCell>
							<TableCell>{{ dollars(credit.remainingCents) }}</TableCell>
							<TableCell><code class="text-muted-foreground text-xs">{{ credit.id }}</code></TableCell>
							<TableCell>
								<Button
									v-if="open"
									type="button"
									variant="outline"
									size="sm"
									:disabled="busy"
									data-testid="staff-remove-credit"
									@click="askToRemove({ kind: 'credit', credit })"
								>
									Remove
								</Button>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else compact data-testid="staff-credits-empty">No credits.</ZhEmptyState>
			</ZhPanel>

			<ZhPanel class="space-y-2">
				<ZhSectionHeading as="h2">Reconciliation</ZhSectionHeading>
				<p v-if="!detail.reconciliation.length" class="text-muted-foreground text-sm">No closed periods yet.</p>
				<p v-else class="text-sm">
					{{ detail.reconciliation.length }} closed period units.
					<RouterLink to="/staff/reconciliation" class="underline underline-offset-4">See them all</RouterLink>
				</p>
			</ZhPanel>

			<ZhPanel class="space-y-2">
				<ZhSectionHeading as="h2">Staff actions</ZhSectionHeading>
				<Table v-if="detail.actions.length">
					<TableHeader>
						<TableRow>
							<TableHead>When</TableHead>
							<TableHead>Action</TableHead>
							<TableHead>Before</TableHead>
							<TableHead>After</TableHead>
							<TableHead>Reason</TableHead>
							<TableHead>By</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="action in detail.actions" :key="action.id" data-testid="staff-action-row">
							<TableCell>{{ action.createdAt }}</TableCell>
							<TableCell>{{ staffActionWords(action.action) }}</TableCell>
							<TableCell>{{ staffChangeWords(action.action, action.before) }}</TableCell>
							<TableCell>{{ staffChangeWords(action.action, action.after) }}</TableCell>
							<TableCell>{{ action.reason ?? '' }}</TableCell>
							<TableCell><code>{{ action.actorHumanUserId }}</code></TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else>No staff action yet.</ZhEmptyState>
			</ZhPanel>
		</template>

		<Dialog :open="dialog === 'pause'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Pause {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						Nothing new runs until it is unpaused. Its people can still sign in, and see a banner saying it is
						paused. The reason is kept in the staff record only.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-pause-reason">Reason</Label>
					<Textarea id="staff-pause-reason" v-model="reason" rows="3" />
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" :disabled="busy" data-testid="staff-pause-submit" @click="pause">Pause</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'plan'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Set the plan of {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						By hand, the plan and its limits change now and FloPay is not asked. Through FloPay, the plan
						changes when FloPay confirms it.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label id="staff-plan-id-label">Plan</Label>
					<Select v-model="planId">
						<SelectTrigger class="w-full" aria-labelledby="staff-plan-id-label" data-testid="staff-plan-id">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="plan in PLANS" :key="plan.id" :value="plan.id">{{ plan.name }}</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div class="space-y-1">
					<Label id="staff-plan-kind-label">How</Label>
					<Select v-model="planKind">
						<SelectTrigger class="w-full" aria-labelledby="staff-plan-kind-label" data-testid="staff-plan-kind">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="kind in KINDS" :key="kind.id" :value="kind.id">{{ kind.name }}</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div v-if="planKind === 'quoted'" class="space-y-1">
					<Label for="staff-plan-price">Quoted price, US dollars a month</Label>
					<Input id="staff-plan-price" v-model="priceDollars" type="number" min="0" step="0.01" />
				</div>
				<div v-if="planKind === 'trial'" class="space-y-1">
					<Label for="staff-plan-ends">Trial ends on</Label>
					<Input id="staff-plan-ends" v-model="endsOn" type="date" />
				</div>
				<div class="space-y-1">
					<Label for="staff-plan-reason">Reason</Label>
					<Textarea id="staff-plan-reason" v-model="reason" rows="2" />
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" :disabled="busy" data-testid="staff-set-plan-submit" @click="setPlan">Set plan</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'extend'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Extend the period of {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						A plan set by hand moves its end now. A FloPay subscription asks FloPay, and the period moves when
						FloPay confirms it.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-extend-ends">New end</Label>
					<Input id="staff-extend-ends" v-model="endsOn" type="date" />
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" :disabled="busy" data-testid="staff-extend-submit" @click="extend">Extend</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'close'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Close {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						Sign-in is refused and nothing runs. Its FloPay subscription is cancelled, and its data is erased
						once its plan's retention has passed, unless it is reopened before then.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-close-name">Type the enterprise's name to confirm</Label>
					<Input id="staff-close-name" v-model="confirmName" autocomplete="off" />
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" variant="destructive" :disabled="busy" data-testid="staff-close-submit" @click="close">
						Close enterprise
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'coupon'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Apply a coupon to {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						An existing FloPay coupon goes on its subscription, and FloPay applies the discount. The reason is
						kept in the staff record only: FloPay and the enterprise's people never see it.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-coupon-id">FloPay coupon id (required)</Label>
					<Input
						id="staff-coupon-id"
						v-model="couponId"
						autocomplete="off"
						required
						aria-describedby="staff-coupon-id-hint"
					/>
					<p id="staff-coupon-id-hint" class="text-muted-foreground text-xs" data-testid="staff-coupon-id-hint">
						Copy it from the coupon in FloPay's dashboard. The OS never creates coupons: make a new discount
						there first.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="staff-coupon-reason">Reason (required)</Label>
					<Textarea
						id="staff-coupon-reason"
						v-model="reason"
						rows="2"
						required
						:maxlength="REASON_LIMIT"
						aria-describedby="staff-coupon-reason-hint"
					/>
					<p id="staff-coupon-reason-hint" class="text-muted-foreground text-xs" data-testid="staff-reason-hint">
						Up to {{ REASON_LIMIT }} characters ({{ reason.length }}/{{ REASON_LIMIT }}).
					</p>
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" variant="outline" :disabled="busy" data-testid="staff-dialog-cancel" @click="dialog = null">
						Cancel
					</Button>
					<Button
						type="button"
						:disabled="busy || !couponReady"
						data-testid="staff-apply-coupon-submit"
						@click="applyCoupon"
					>
						{{ busy ? 'Applying…' : 'Apply coupon' }}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'credit'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add a credit for {{ detail?.name }}</DialogTitle>
					<DialogDescription>
						A one-off amount in US dollars, as every plan is priced and this page shows every FloPay amount,
						which FloPay takes off the next invoice. The invoice says "Credit from Zero Human"; the reason is
						kept in the staff record only.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-credit-amount">Amount, US dollars (required)</Label>
					<Input
						id="staff-credit-amount"
						v-model="creditAmount"
						type="number"
						min="0.01"
						step="0.01"
						required
						aria-describedby="staff-credit-amount-hint"
					/>
					<p id="staff-credit-amount-hint" class="text-muted-foreground text-xs" data-testid="staff-credit-amount-hint">
						More than $0.00, to the cent: 50 is $50.00.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="staff-credit-reason">Reason (required)</Label>
					<Textarea
						id="staff-credit-reason"
						v-model="reason"
						rows="2"
						required
						:maxlength="REASON_LIMIT"
						aria-describedby="staff-credit-reason-hint"
					/>
					<p id="staff-credit-reason-hint" class="text-muted-foreground text-xs" data-testid="staff-reason-hint">
						Up to {{ REASON_LIMIT }} characters ({{ reason.length }}/{{ REASON_LIMIT }}).
					</p>
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" variant="outline" :disabled="busy" data-testid="staff-dialog-cancel" @click="dialog = null">
						Cancel
					</Button>
					<Button type="button" :disabled="busy || !creditReady" data-testid="staff-add-credit-submit" @click="addCredit">
						{{ busy ? 'Adding…' : 'Add credit' }}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="dialog === 'remove'" @update:open="closeDialog">
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{{ removingTitle }}</DialogTitle>
					<DialogDescription data-testid="staff-remove-lost">
						<template v-if="removing">{{ removalWords(removing) }}</template>
						The reason is kept in the staff record only.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-1">
					<Label for="staff-remove-reason">Reason (required)</Label>
					<Textarea
						id="staff-remove-reason"
						v-model="reason"
						rows="2"
						required
						:maxlength="REASON_LIMIT"
						aria-describedby="staff-remove-reason-hint"
					/>
					<p id="staff-remove-reason-hint" class="text-muted-foreground text-xs" data-testid="staff-reason-hint">
						Up to {{ REASON_LIMIT }} characters ({{ reason.length }}/{{ REASON_LIMIT }}).
					</p>
				</div>
				<p v-if="actionError" class="text-destructive text-sm" role="alert">{{ actionError }}</p>
				<DialogFooter>
					<Button type="button" variant="outline" :disabled="busy" data-testid="staff-dialog-cancel" @click="dialog = null">
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						:disabled="busy || !reasonReady"
						data-testid="staff-remove-submit"
						@click="remove"
					>
						{{ busy ? 'Removing…' : 'Remove' }}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</section>
</template>
